import React from 'react';

import { Box, ButtonBase, Typography, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { BsCheck } from 'react-icons/bs';

import { DRCProfile, END_USER_STATUSES } from '@vyce/core/src/types';
import { END_USER_OPTIONS } from '@vyce/core/src/constants';
import { getUKFormattedDate } from '@vyce/core/src/utils/dates';

import { useStyles } from '../styles';

interface Props {
  profile: DRCProfile | null;
  disabled?: boolean;
  onChange: (status: END_USER_STATUSES) => void;
}

/**
 * The ruling the whole feature hangs on. Only "Not an end user" makes the reverse charge
 * eligible; picking anything else clears the confirmation stamp on the server.
 */
export const EndUserStatusControl: React.FC<Props> = ({ profile, disabled, onChange }) => {
  const classes = useStyles();
  const theme = useTheme();
  const value = profile?.end_user_status ?? END_USER_STATUSES.NOT_ASSESSED;
  const confirmed = value === END_USER_STATUSES.NOT_END_USER;

  return (
    <Box>
      <Box display="flex" alignItems="baseline" justifyContent="space-between" gridGap={8} flexWrap="wrap">
        <Typography className={classes.sectionLabel}>End-user status</Typography>
        <Typography className={classes.hint}>
          Only “Not an end user” makes the reverse charge eligible
        </Typography>
      </Box>

      <Box className={classes.optionsRow}>
        {END_USER_OPTIONS.map(option => {
          const active = value === option.value;
          return (
            <ButtonBase
              key={option.value}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={clsx(classes.optionCard, {
                [classes.optionCardActive]: active && !option.eligible,
                [classes.optionCardEligible]: active && option.eligible,
              })}>
              <Box
                width={18}
                height={18}
                borderRadius="50%"
                flexShrink={0}
                marginTop="2px"
                display="grid"
                style={{ placeItems: 'center' }}
                bgcolor={active ? (option.eligible ? 'success.main' : 'primary.main') : 'transparent'}
                border={active ? 0 : 1.5}
                borderColor="divider">
                {active && <BsCheck size="14px" color={theme.palette.common.white} />}
              </Box>
              <Box>
                <Typography variant="subtitle2">{option.label}</Typography>
                <Typography className={classes.hint}>{option.hint}</Typography>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>

      {confirmed && (
        <Box marginTop={1}>
          {/*
            The stamp is written from the authenticated token and the name comes back beside the
            id on every route that answers a profile — printed straight from the response, with
            the id as a fallback for the rare case the identity service could not name the user.
          */}
          <Typography className={classes.hint}>
            {`Confirmed by ${profile?.end_user_confirmed_by_name || profile?.end_user_confirmed_by || '—'}`}
            {profile?.end_user_confirmed_at ? ` · ${getUKFormattedDate(profile.end_user_confirmed_at)}` : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
